import { useCallback, useEffect, useMemo, useState } from 'react';
import settings from '~/settings';

/**
 * Headless selection manager for row IDs.
 * rows: array of { id }
 * limit: optional cap on selected IDs
 * onChange: optional callback(ids[])
 */
export default function useRowSelection({ rows, limit = settings.bulkActionItemLimit, onChange }) {
  const allIds = useMemo(() => (rows ?? []).map((r) => r.id), [rows]);
  const [ids, setIds] = useState([]);

  // Ensure selection stays valid when rows/limit change
  useEffect(() => {
    setIds((prev) => prev.filter((id) => allIds.includes(id)).slice(0, limit));
  }, [allIds, limit]);

  // Centralized setter that enforces uniqueness, membership, and limit
  const set = useCallback(
    (next) => {
      setIds((prev) => {
        const raw = typeof next === 'function' ? next(prev) : next;
        const unique = Array.from(new Set(raw))
          .filter((id) => allIds.includes(id))
          .slice(0, limit);
        onChange?.(unique);
        return unique;
      });
    },
    [allIds, limit, onChange],
  );

  const clear = useCallback(() => set([]), [set]);

  const select = useCallback(
    (id) => set((curr) => (curr.includes(id) || curr.length >= limit ? curr : [...curr, id])),
    [set, limit],
  );

  const deselect = useCallback((id) => set((curr) => curr.filter((x) => x !== id)), [set]);

  const toggle = useCallback(
    (id) =>
      set((curr) => (curr.includes(id) ? curr.filter((x) => x !== id) : curr.length >= limit ? curr : [...curr, id])),
    [set, limit],
  );

  const selectMany = useCallback(
    (addIds) =>
      set((curr) => {
        const next = [...curr];
        for (const id of addIds) {
          if (next.length >= limit) break;
          if (allIds.includes(id) && !next.includes(id)) next.push(id);
        }
        return next;
      }),
    [set, limit, allIds],
  );

  const selectAll = useCallback(() => set(allIds.slice(0, limit)), [set, allIds, limit]);

  const toggleAll = useCallback(
    () => set((curr) => (curr.length > 0 ? [] : allIds.slice(0, limit))),
    [set, allIds, limit],
  );

  const isSelected = useCallback((id) => ids.includes(id), [ids]);

  const count = ids.length;
  const total = allIds.length;
  const any = count > 0;
  const all = total > 0 && count === Math.min(total, limit);
  const some = any && !all;
  const limitReached = count >= limit;

  const isDisabled = useCallback((id) => !ids.includes(id) && limitReached, [ids, limitReached]);

  return {
    // state
    ids,
    count,
    total,
    limit,

    // derived flags
    any,
    all,
    some,
    limitReached,

    // queries
    isSelected,
    isDisabled,

    // mutations
    select,
    deselect,
    toggle,
    selectMany,
    selectAll,
    toggleAll,
    clear,
  };
}
